import Column from '@components/Column';
import Text from '@components/Text';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { OrderResponse } from '@models/order/orderLog';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    order: OrderResponse;
}

function Notes({ order }:Props) {
    const { t } = useTranslation('orderLog');
    const {
        paymentMethod, totalAmount, pgAmount, giftAmount, prepaidCardAmount, discounts, status, note,
    } = order;
    const priceUnit = usePriceUnit();
    const isCancelled = status === 'CANCELLED';

    const employeeDiscount = discounts
        .find((it) => it.discountType === 'EMPLOYEE');

    const couponDiscount = discounts
        .filter((it) => it.discountType === 'COUPON')
        .reduce((acc, cur) => acc + cur.discountAmount, 0);

    const pointDiscount = discounts
        .filter((it) => it.discountType === 'POINT')
        .reduce((acc, cur) => acc + cur.discountAmount, 0);

    const deductions = [
        { label: t('notes.employeeDiscount'), amount: employeeDiscount?.discountAmount ?? 0 },
        { label: t('notes.couponDiscount'), amount: couponDiscount },
        { label: t('notes.point'), amount: pointDiscount },
        { label: t('notes.gift'), amount: giftAmount },
        { label: t('notes.prepaidCard'), amount: prepaidCardAmount },
    ].filter((it) => it.amount > 0);

    return (
        <Container>
            {note && (
                <Card>
                    <Text size='title' fontWeight='bold'>{t('notes.requestTitle')}</Text>
                    <NoteText size='body-long' color='var(--color-text-secondary)'>
                        {note}
                    </NoteText>
                </Card>
            )}
            <Card>
                <Text size='title' fontWeight='bold'>{t('notes.paymentTitle')}</Text>
                <Grid>
                    <Text color='var(--color-text-secondary)' as='span'>{t('notes.paymentMethod')}</Text>
                    <Text color='var(--color-text-secondary)' as='span' align='right'
                        css={isCancelled && canceledCss}>
                        {t(`paymentMethod.${paymentMethod}`)}
                    </Text>
                    <Text color='var(--color-text-secondary)'>{t('notes.productAmount')}</Text>
                    <Text css={isCancelled && canceledCss} align='right' color='var(--color-text-secondary)'>
                        {totalAmount.toLocaleString()}{priceUnit}
                    </Text>
                    {deductions.map(({ label, amount }) => (
                        <Fragment key={label}>
                            <Text color='var(--color-text-secondary)'>{label}</Text>
                            <Text css={isCancelled && canceledCss} color='var(--color-text-secondary)' align='right'>
                                -{amount.toLocaleString()}{priceUnit}
                            </Text>
                        </Fragment>
                    ))}
                    <Divider/>
                    <Text size='title' fontWeight='bold'>{t('notes.paymentAmount')}</Text>
                    <Text size='headline' fontWeight='bold' align='right'
                        css={isCancelled && canceledCss}>
                        {pgAmount.toLocaleString()}{priceUnit}
                    </Text>
                </Grid>
            </Card>
        </Container>
    );
}

const canceledCss = css`
    text-decoration: line-through;
`;

const Divider = styled.div`
    grid-column-start: 1;
    grid-column-end: 3;
    width: 100%;
    height: 1px;
    background-color: var(--color-background-raised);
`;

const Container = styled(Column)`
    width: 100%;
    padding-bottom: 24px;
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    background-color: var(--color-white);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    margin: var(--spacing-4) var(--spacing-4) 0;
    padding: var(--spacing-4);
    width: calc(100% - var(--spacing-8));
`;

const NoteText = styled(Text)`
    white-space: pre-wrap;
    word-break: break-word;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    width: 100%;
    gap: 8px;
`;

export default Notes;
